import { getValue } from '@syncfusion/ej2-base';
import { RowDataBoundEventArgs } from '@syncfusion/ej2-grids';
import { ColumnModel, GridComponent, IIndex, QueryCellInfoEventArgs } from '@syncfusion/ej2-react-grids';
import { RefObject } from 'react';
import { ICurrentCellEventArgs } from '../data-types/ICurrentCellEventArgs';
import {
    ColumnDto,
    ColumnGridDataType,
    DetailedWorkspaceDtoColumnSchema,
    DetailedWorkspaceDtoRowSchema,
    IWhitePaperColumn,
    IWhitePaperComparedValue,
    IWhitePaperRow,
    WhitePaperRowStyle,
} from '../model';
import { getValueWithoutDollarSign, parseNumericValue } from './NumberUtils';
import { TOTAL_ROW_NUMBER } from './Table.constants';

export const WHITE_PAPER_CELL_BLUE_CLASS = 'white-paper-cell-blue';
export const WHITE_PAPER_CELL_BOLD_CLASS = 'white-paper-cell-bold';

export const BEGIN_EDIT_REQUEST_TYPE = 'beginEdit';
export const SAVE_REQUEST_TYPE = 'save';
export const CANCEL_REQUEST_TYPE = 'cancel';

export const TEXT_PLAIN_CLIPBOARD_CONTENT_TYPE = 'text/plain';
export const TEXT_HTML_CLIPBOARD_CONTENT_TYPE = 'text/html';
export const TEXT_RTF_CLIPBOARD_CONTENT_TYPE = 'text/rtf';
export const FILES_CLIPBOARD_CONTENT_TYPE = 'Files';

const GRID_FOCUSED_CELL_CLASS = 'e-focused';
const GRID_FOCUS_CELL_CLASS = 'e-focus';
const GRID_CELL_ROW_INDEX_ATTRIBUTE_NAME = 'index';
const GRID_CELL_COL_INDEX_ATTRIBUTE_NAME = 'data-colindex';
const DISTANCE_TO_ENTITY_VARIANCE_COLUMN = 2;

export const formatAmount = (amount: number | null): string => {
    if (amount === null) {
        return '';
    }

    const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    if (amount < 0) {
        return '(' + formatter.format(amount).replace('-', '') + ')';
    }

    return formatter.format(amount);
};

export const addStylesToTotalRow = (args: RowDataBoundEventArgs) => {
    if (args.row) {
        if (getValue('displayRowNumber', args.data) === TOTAL_ROW_NUMBER) {
            args.row.classList.add('bg-total');
        }
    }
};

export const getHeaderDateBasedOnColumnId = (compareDate: string): string => {
    const options: any = { month: 'numeric', day: 'numeric', year: '2-digit' };
    const timeOptions: any = { hour: '2-digit', minute: '2-digit' };
    if (compareDate === '') {
        return new Date().toLocaleString('en-us', options) + ' - ' + new Date().toLocaleTimeString('en-us', timeOptions);
    }
    return new Date(compareDate).toLocaleString('en-us', options) + ' - ' + new Date(compareDate).toLocaleTimeString('en-us', timeOptions);
};

export const getAmountAsNumberOrDefault = (reportRow: IWhitePaperRow, columnFieldName: string): string | null => {
    return getCellValueForColumnInWhitePaperRow(
        reportRow,
        columnFieldName,
        (columnValue: IWhitePaperComparedValue) => columnValue.current ?? null
    );
};

export const getCellValueForColumnInWhitePaperRow = <T>(
    reportRow: IWhitePaperRow,
    columnName: string,
    cellValueSelector: (columnValue: IWhitePaperComparedValue) => T
): T | null => {
    if (reportRow.values) {
        const columnValue: IWhitePaperComparedValue = reportRow.values[columnName];
        if (columnValue) {
            return cellValueSelector(columnValue);
        }
    }

    return null;
};

export const getClassNameForStyle = (style: WhitePaperRowStyle): string => {
    switch (style) {
        case WhitePaperRowStyle.Blue:
            return WHITE_PAPER_CELL_BLUE_CLASS;
        case WhitePaperRowStyle.Bold:
            return WHITE_PAPER_CELL_BOLD_CLASS;
    }
};

export const setFocusOnSyncfusionGridCell = (cellToSelect: HTMLElement | undefined): void => {
    if (cellToSelect) {
        cellToSelect.classList.add(GRID_FOCUSED_CELL_CLASS, GRID_FOCUS_CELL_CLASS);
        cellToSelect.click();
        cellToSelect.focus();
    }
};

export const getIndexObjectBasedOnCellElement = (element: HTMLElement): IIndex => {
    return {
        rowIndex: parseInt(element.getAttribute(GRID_CELL_ROW_INDEX_ATTRIBUTE_NAME) ?? '0'),
        cellIndex: parseInt(element.getAttribute(GRID_CELL_COL_INDEX_ATTRIBUTE_NAME) ?? '0'),
    };
};

export const getCellHtmlElementByIndex = (rowIndex: number, cellIndex: number): HTMLElement | null => {
    const containerElement: Document = document;
    return containerElement.querySelector(
        `td[${GRID_CELL_ROW_INDEX_ATTRIBUTE_NAME}="${rowIndex}"][${GRID_CELL_COL_INDEX_ATTRIBUTE_NAME}="${cellIndex}"]`
    );
};

export const getGridCellHtmlElementByIndex = (
    grid: GridComponent | null,
    rowIndex: number,
    cellIndex: number,
    isVariance: boolean = false
): HTMLElement | null => {
    const containerElement: Element | Document = grid ? grid.getContent() : document;

    const targetColumn: ColumnModel = grid?.columns[cellIndex] as ColumnModel;
    // With column virtualization, for some reason the col-index property on cells changes and it does not match the column index
    // We are getting the column information to find the DOM element based on that
    if (targetColumn) {
        const matchingEntityColumnHeaders = Array.from(document.querySelectorAll(`div[e-mappinguid="${targetColumn.uid}"]`));
        const targetColumnHeader = matchingEntityColumnHeaders.filter((header) => {
            return (header as HTMLElement).outerText === targetColumn.headerText;
        })[0];

        if (!targetColumnHeader) return null;

        const targetColumnHeaderTD = (targetColumnHeader as HTMLElement)?.parentElement as HTMLElement;
        let targetColumnHeaderIndex = parseInt(targetColumnHeaderTD?.getAttribute(GRID_CELL_COL_INDEX_ATTRIBUTE_NAME) ?? '0');
        if (isVariance && containerElement && targetColumnHeaderIndex >= 0) {
            targetColumnHeaderIndex += DISTANCE_TO_ENTITY_VARIANCE_COLUMN;
            return containerElement.querySelector(
                `td[${GRID_CELL_ROW_INDEX_ATTRIBUTE_NAME}="${rowIndex}"][${GRID_CELL_COL_INDEX_ATTRIBUTE_NAME}="${targetColumnHeaderIndex}"]`
            );
        }
        return getCellHtmlElementByIndex(rowIndex, targetColumnHeaderIndex);
    }

    return null;
};

export const getCellByColumnIndex = (columnIndex: number, cells: NodeListOf<Element> | undefined): HTMLElement | null => {
    if (cells) {
        return (Array.from(cells).find((cell) => cell.getAttribute('data-colindex') === columnIndex.toString()) as HTMLElement) || null;
    }

    return null;
};

export const focusCellByIndex = (cellIndex: IIndex) => {
    const newCell = getCellHtmlElementByIndex(cellIndex.rowIndex ?? 0, cellIndex.cellIndex ?? 0) as HTMLElement;

    setFocusOnSyncfusionGridCell(newCell);
    newCell?.click();
};

export const focusOnButtonContainedByCell = (cellIndex: IIndex) => {
    const newCell = getCellHtmlElementByIndex(cellIndex.rowIndex ?? 0, cellIndex.cellIndex ?? 0) as HTMLElement;

    if (newCell) {
        const childButton = newCell.querySelector('button') as HTMLElement;
        childButton.focus();
    }
};

export const clickOnVarianceContainedByCell = (grid: GridComponent | null, cellIndex: IIndex) => {
    const varianceCell = getGridCellHtmlElementByIndex(grid, cellIndex.rowIndex ?? 0, cellIndex.cellIndex ?? 0, true) as HTMLElement;
    if (varianceCell) {
        setFocusOnSyncfusionGridCell(varianceCell);
        varianceCell.click();
    }
};

export const focusOnFirstCellFromRow = (rowIndex: number, grid?: GridComponent) => {
    if (grid) {
        const cellToSelect = getGridCellHtmlElementByIndex(grid, rowIndex, 0) as HTMLElement;

        setFocusOnSyncfusionGridCell(cellToSelect);
    }
};

export const getFirstFocusableElementInForm = (form: HTMLFormElement): HTMLInputElement => {
    const elements = Array.from(form.elements);

    return elements.find((element) => !(element as HTMLInputElement).disabled) as HTMLInputElement;
};

export const isRowInCache = (gridComponent: GridComponent, rowIndex: number): boolean => {
    const blockIndexes = ((gridComponent.contentModule as any).prevInfo as any).blockIndexes;
    const cache = (gridComponent.contentModule as any).vgenerator.cache;
    let isRowInCache = false;

    for (let i = 0; i < blockIndexes.length; i++) {
        if (isRowInCache) {
            break;
        }

        isRowInCache = rowExistsInCacheBlock(rowIndex, cache[blockIndexes[i]]);
    }

    return isRowInCache;
};

const rowExistsInCacheBlock = (rowIndex: number, cacheBlockRows: any[]): boolean => {
    for (let j = 0; j < cacheBlockRows.length; j++) {
        if (cacheBlockRows[j].index === rowIndex) {
            return true;
        }
    }

    return false;
};

/** DJB2 hash: maps arbitrary text to a stable 32-bit unsigned integer for compact grid key suffixes. */
const stringToFixedInt = (text: string): number => {
    let hash = 5381;

    for (let index = 0; index < text.length; index++) {
        hash = (hash * 33) ^ text.charCodeAt(index);
    }

    return hash >>> 0;
};

const hashColumnHeaders = (columns: ColumnModel[] | undefined): string => {
    if (!columns?.length) {
        return '';
    }

    const headerKey = columns.map((column) => column.headerText ?? '').join('-');
    return stringToFixedInt(headerKey).toString();
};

export const getTableKeyWithVisibleFields = (visibleFields: string[], versionId: string, columns?: ColumnModel[]): string => {
    const visibleFieldsHash = visibleFields
        ? visibleFields.map((fieldName: string) => fieldName.charAt(fieldName.length / 2)).join('')
        : '';
    const columnHeadersHash = hashColumnHeaders(columns);
    return `tax-return-grid-${visibleFieldsHash}-${columnHeadersHash}${versionId}`;
};

export const getLegacyTableKeyWithColumnsFilters = (aliasTable: string, columnsFilters: Record<string, any[]>): string => {
    const columnValueHash = JSON.stringify(columnsFilters);
    return `${aliasTable}${columnValueHash}`;
};

export const scrollToTop = (grid: GridComponent | null) => {
    if (grid && grid.getContent().children[0]) {
        grid.getContent().children[0].scrollTop = 0;
    }
};

export const scrollToRow = (grid: GridComponent | null, rowIndex: number): number | null => {
    if (grid && grid.getContent().children[0]) {
        const rowHeight = grid.rowHeight || 0;
        const isRowRendered = isRowInCache(grid, rowIndex);

        if (isRowRendered) {
            grid.selectRow(rowIndex);
        } else {
            grid.getContent().children[0].scrollTop = rowHeight * rowIndex;
            return rowIndex;
        }
    }

    return null;
};

export const scrollToX = (grid: GridComponent | null, xPosition: number) => {
    if (grid && grid.getContent().children[0]) {
        grid.getContent().children[0].scrollLeft = xPosition;
    }
};

export const highlightSubstringWithClass = (
    cell: Element,
    substring: string,
    className: string,
    shouldSearchDeepestChild: boolean = false
) => {
    if (!substring) return;

    const target = shouldSearchDeepestChild ? getTemplateCellDeepestChild(cell, substring) : cell;

    if (!target || !target.textContent?.toLowerCase().includes(substring.toLowerCase())) return;

    target.innerHTML = target.innerHTML.replaceAll(substring, `<span class='${className}'>${substring}</span>`);
};

export const getTemplateCellDeepestChild = (element: Element, searchString: string): Element => {
    let deepestChild: Element = element;
    let maxDepth: number = 0;

    const traverse = (node: Element, depth: number): void => {
        if (node.innerHTML.includes(searchString)) {
            if (depth > maxDepth) {
                deepestChild = node;
                maxDepth = depth;
            }
            Array.from(node.children).forEach((child: Element) => {
                traverse(child, depth + 1);
            });
        }
    };

    traverse(element, 0);
    return deepestChild;
};

export const shouldUpdateAriaLabel = (args: QueryCellInfoEventArgs): boolean => {
    return args.column !== undefined && args.column.editType === 'numericedit';
};

export const getWhitePaperReportHeaderTemplate = (column: IWhitePaperColumn): string | undefined => {
    if (column.headingFirstLine && column.headingSecondLine) {
        return `<div class="headerTextWrap">
            <label>${column.headingFirstLine}</label>
            <label>${column.headingSecondLine}</label>
        </div>`;
    }

    return undefined;
};

export const handleSearchAction = (
    gridRef: GridComponent | null,
    setIsDisabledVarianceGoToNavigation: (value: boolean) => void,
    setCurrentVarianceIndex: (value: number) => void
) => {
    const emptySearch: boolean = !gridRef || gridRef.searchSettings.key === '';
    setIsDisabledVarianceGoToNavigation(!emptySearch);
    setCurrentVarianceIndex(0);

    if (gridRef) {
        scrollToTop(gridRef);
    }
};

export const calculateNextVarianceStep = (currentVarianceIndex: number, variancePointsLength: number, isNext: boolean): number => {
    let step = currentVarianceIndex;

    if (isNext) {
        step += 1;
    } else {
        step -= 1;
    }

    if (step <= 0) {
        step = variancePointsLength;
    }

    if (step > variancePointsLength) {
        step = 1;
    }
    return step;
};

export const isExcelClipboardContent = (event: ClipboardEvent): boolean => {
    return (
        event.clipboardData?.types !== undefined &&
        event.clipboardData.types.includes(TEXT_PLAIN_CLIPBOARD_CONTENT_TYPE) &&
        event.clipboardData.types.includes(TEXT_HTML_CLIPBOARD_CONTENT_TYPE) &&
        event.clipboardData.types.includes(TEXT_RTF_CLIPBOARD_CONTENT_TYPE) &&
        event.clipboardData.types.includes(FILES_CLIPBOARD_CONTENT_TYPE)
    );
};

export const getColumnNameFromCurrentComparedColumnName = (eventArgs: ICurrentCellEventArgs<IWhitePaperRow>) => {
    return eventArgs?.fieldName?.replace('values.', '').replace('.current', '');
};

export const getInputNameFromClipboardEvent = (event: ClipboardEvent) => {
    return (event.target as HTMLElement)?.getAttribute('name') ?? '';
};

export const getAffectedColumns = (columns: ColumnDto[], initialColumnFieldName: string) => {
    return columns.slice(columns.findIndex((col) => col.field === initialColumnFieldName));
};

export const getAffectedRows = (
    dataSource: Record<string, unknown>[],
    selectedRowKey: number,
    rowDefinitions?: DetailedWorkspaceDtoRowSchema
) => {
    const affectedRows = dataSource.slice(dataSource.findIndex((row) => row.RowNo === selectedRowKey));

    if (rowDefinitions) {
        return affectedRows.filter((row) => !rowDefinitions[row.RowNo as number]?.disable);
    }

    return affectedRows;
};

export const getCellPastedValue = (cellValue: string | undefined, dataType?: string | null, maxLength?: number | null) => {
    if (maxLength) {
        cellValue = cellValue?.slice(0, maxLength);
    }

    if (dataType === 'int') {
        cellValue = getValueWithoutDollarSign(cellValue);
        const parsedNumericValue = cellValue ? parseNumericValue(cellValue) : 0;
        return isNaN(parsedNumericValue) ? 0 : parsedNumericValue;
    } else {
        return cellValue;
    }
};

export const parsePasteContentToTable = (
    pastedText: string,
    columns: ColumnDto[],
    selectedRowKey: number,
    initialColumnFieldName: string,
    columnSchemas: DetailedWorkspaceDtoColumnSchema | undefined,
    dataSource: Record<string, unknown>[]
): { newRows: Record<string, unknown>[]; changedRows: Record<string, unknown>[] } => {
    // This will literally be any type of data, can be number, string, date, etc.
    const newRows: Record<string, unknown>[] = [];
    const changedRows: Record<string, unknown>[] = [];
    const rowsText = pastedText.split('\r\n');
    const affectedRows = getAffectedRows(dataSource, selectedRowKey);
    const affectedColumns = getAffectedColumns(columns, initialColumnFieldName);
    let highestRowNo = 0;

    for (let i = 0; i < rowsText.length; i++) {
        if (rowsText[i].length > 0) {
            const cells = rowsText[i].split('\t');
            const row: Record<string, unknown> = {};
            let isNewRow = false;

            if (affectedRows.length > i) {
                highestRowNo = affectedRows[i].RowNo as number;
            } else {
                isNewRow = true;
                highestRowNo += 1;
            }

            row.RowNo = highestRowNo;

            for (let j = 0; j < affectedColumns.length; j++) {
                const columnField = affectedColumns[j].field ?? '';
                const entry = columnSchemas?.[columnField];
                row[columnField] = getCellPastedValue(cells[j], entry?.type, entry?.maxLength);
            }

            if (isNewRow) {
                newRows.push(row);
            } else {
                changedRows.push(row);
            }
        }
    }

    return { newRows, changedRows };
};

export const parsePasteContentToColumnarReportTable = (
    pastedText: string,
    columns: ColumnDto[],
    affectedColumnsNames: string[],
    rows: Record<string, unknown>[],
    rowDefinitions: DetailedWorkspaceDtoRowSchema | undefined
): Record<string, any>[] => {
    const result = [...rows];

    // Remove the last appearance of '\r\n' in pastedText, to avoid a trailing line break
    const lastLineBreakIndex = pastedText.lastIndexOf('\r\n');
    let trimmedText = pastedText;
    if (lastLineBreakIndex !== -1 && lastLineBreakIndex === pastedText.length - 2) {
        trimmedText = pastedText.slice(0, lastLineBreakIndex);
    }
    const rowsText = trimmedText.split('\r\n');

    if (rowsText.length > 0) {
        for (let i = 0; i < result.length; i++) {
            if (rowsText.length <= i) {
                break;
            }

            const cells = rowsText[i].split('\t');
            const row = result[i];
            const rowDefinition = rowDefinitions?.[row.RowNo as number];
            const pasteDataType = rowDefinition?.dataType === ColumnGridDataType.Numeric ? 'int' : undefined;
            let maxColumnNumber = columns.length - 1;

            for (let j = 0; j < cells.length; j++) {
                if (j < affectedColumnsNames.length) {
                    row[affectedColumnsNames[j]] = getCellPastedValue(cells[j], pasteDataType, rowDefinition?.maxLength);
                } else {
                    const newColumnFieldName = `C${maxColumnNumber}`;
                    maxColumnNumber++;

                    row[newColumnFieldName] = getCellPastedValue(cells[j], pasteDataType, rowDefinition?.maxLength);
                }
            }
        }
    }

    return result;
};

export const enableSingleClickEditOnCells = (gridRef: RefObject<GridComponent>, editCallback: (cell: HTMLElement) => void) => {
    const grid = gridRef.current;
    if (!grid) return;

    grid.getContentTable().addEventListener('click', function (args: any) {
        editCallback(args.target as HTMLElement);
    });

    grid.element.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.code === 'Enter') {
            const eventTarget = e.target as HTMLElement;
            if (eventTarget.tagName !== 'INPUT' && eventTarget.tagName !== 'TEXTAREA') {
                const closestCell = eventTarget.closest('.e-rowcell') as HTMLElement;
                if (closestCell) {
                    e.preventDefault();
                    editCallback(closestCell);
                }
            }
        }
    });
};
